import { Box, Image, Button, HStack, Flex } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { LuRotateCcw } from 'react-icons/lu';
type DraggableImageBoxProps = {
  src: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  boxProps?: any;
  imageProps?: any;
  showResetButton?: boolean;
  onPositionChange?: (position: {
    x: number;
    y: number;
  }) => void;
  onDragEnd?: (position: {
    x: number;
    y: number;
  }) => void;
  initialPosition?: {
    x: number;
    y: number;
  };
  disableDragEnd?: boolean;
};
export const DraggableImageBox: React.FC<DraggableImageBoxProps> = ({
  src,
  alt = 'Draggable Image',
  width = '100%',
  height = 300,
  boxProps,
  imageProps,
  showResetButton = false,
  onPositionChange,
  onDragEnd,
  initialPosition,
  disableDragEnd = false
}) => {
  const [position, setPosition] = useState(initialPosition || {
    x: 0,
    y: 0
  });
  const [dragging, setDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({
    x: 0,
    y: 0
  });
  const [startPos, setStartPos] = useState({
    x: 0,
    y: 0
  });
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0
  });
  const boxRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [hasUserDragged, setHasUserDragged] = useState(false);
  const prevInitialPositionRef = useRef(initialPosition);
  const isUpdatingFromParentRef = useRef(false);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  useEffect(() => {
    // Removed console.log
  }, [initialPosition]);

  // Calculate bounds for the image
  const getBounds = useCallback(() => {
    if (!boxRef.current) {
      return {
        minX: 0,
        maxX: 0,
        minY: 0,
        maxY: 0
      };
    }
    const boxRect = boxRef.current.getBoundingClientRect();
    const boxWidth = boxRect.width;
    const boxHeight = boxRect.height;

    // For Y-axis only dragging, X is always centered (0)
    const minX = 0;
    const maxX = 0;

    // The preview Box is positioned at top: 50% with transform: translateY(-50%)
    // within the outer 16:9 container. We need to calculate the preview Box's
    // actual dimensions relative to the draggable container.

    // Since the preview Box has aspectRatio={'16/5'} and is centered in a 16:9 container,
    // we need to calculate its actual height based on the container width.
    // The preview Box width should match the container width, so:
    const previewBoxWidth = boxWidth;
    const previewBoxHeight = previewBoxWidth * 5 / 16; // 16:5 aspect ratio

    // Calculate the preview Box's position relative to the draggable container
    // The preview Box is centered in the outer container, so it might be smaller
    // than the draggable container
    const previewBoxTop = (boxHeight - previewBoxHeight) / 2;
    const previewBoxBottom = previewBoxTop + previewBoxHeight;

    // Round the values to avoid floating point precision issues
    const roundedPreviewBoxTop = Math.round(previewBoxTop * 100) / 100;
    const roundedPreviewBoxBottom = Math.round(previewBoxBottom * 100) / 100;

    // For cover photo use case: ensure image can be positioned so that:
    // - Top of image can align with top of preview Box
    // - Bottom of image can align with bottom of preview Box
    // Use a more stable approach: calculate bounds based on preview box dimensions
    // rather than current image height. Use a fixed multiplier to ensure consistency.
    const minY = roundedPreviewBoxBottom - previewBoxHeight * 1.8; // Allow image to be 1.8x preview height
    const maxY = roundedPreviewBoxTop;

    // Debug: Log bounds calculation
    console.log('Bounds calculation:', {
      boxHeight,
      imageSizeHeight: imageSize.height,
      previewBoxHeight,
      previewBoxTop,
      previewBoxBottom,
      roundedPreviewBoxTop,
      roundedPreviewBoxBottom,
      minY,
      maxY,
      draggableRange: minY - maxY
    });
    return {
      minX,
      maxX,
      minY,
      maxY
    };
  }, [imageSize]);

  // Convert pixel position to percentage (0-100)
  const convertPositionToPercentage = useCallback((pos: {
    x: number;
    y: number;
  }) => {
    if (!boxRef.current || imageSize.height === 0) {
      return 0;
    }
    const bounds = getBounds();

    // If image fits in container, center it
    if (bounds.minY >= bounds.maxY) {
      return 50;
    } else {
      // Image is taller than container - map position to percentage
      // When pos.y = bounds.maxY (image top at container top), we want 0%
      // When pos.y = bounds.minY (image bottom at container bottom), we want 100%

      // The draggable range is from bounds.maxY to bounds.minY
      const draggableRange = bounds.minY - bounds.maxY;

      // Map position within bounds to 0-100%
      // When pos.y = bounds.maxY (top), return 0%
      // When pos.y = bounds.minY (bottom), return 100%
      const yPercentage = Math.max(0, Math.min(100, (pos.y - bounds.maxY) / draggableRange * 100));

      // Round to 1 decimal place for more stability
      const roundedPercentage = Math.round(yPercentage * 10) / 10;

      // Debug: Log conversion details
      console.log('convertPositionToPercentage:', {
        posY: pos.y,
        boundsMaxY: bounds.maxY,
        boundsMinY: bounds.minY,
        draggableRange,
        calculatedPercentage: yPercentage,
        roundedPercentage: Math.round(yPercentage * 10) / 10
      });
      return roundedPercentage;
    }
  }, [getBounds]);

  // Convert percentage position to pixel coordinates
  const convertPercentageToPixels = useCallback((percentagePos: {
    x: number;
    y: number;
  }) => {
    if (!boxRef.current || imageSize.height === 0) {
      return {
        x: 0,
        y: 0
      };
    }
    const bounds_0 = getBounds();

    // If image fits in container, center it
    if (bounds_0.minY >= bounds_0.maxY) {
      return {
        x: 0,
        y: (bounds_0.minY + bounds_0.maxY) / 2
      };
    } else {
      // Image is taller than container - map percentage to pixel position
      // When percentage = 0%, we want y = bounds.maxY (image top at container top)
      // When percentage = 100%, we want y = bounds.minY (image bottom at container bottom)

      // The draggable range is from bounds.maxY to bounds.minY
      const draggableRange_0 = bounds_0.minY - bounds_0.maxY;

      // Map percentage to position within bounds
      const yPixels = bounds_0.maxY + percentagePos.y / 100 * draggableRange_0;

      // Round to 1 decimal place for more stability
      const roundedPixels = Math.round(yPixels * 10) / 10;

      // Debug: Log conversion details
      console.log('convertPercentageToPixels:', {
        percentageY: percentagePos.y,
        boundsMaxY: bounds_0.maxY,
        boundsMinY: bounds_0.minY,
        draggableRange: draggableRange_0,
        calculatedPixels: yPixels,
        roundedPixels: Math.round(yPixels * 10) / 10
      });
      return {
        x: 0,
        y: roundedPixels
      };
    }
  }, [getBounds]);

  // Update position when initialPosition changes
  useEffect(() => {
    if (!dragging && !isUpdatingFromParentRef.current) {
      const prevPosition = prevInitialPositionRef.current;
      const hasChanged = prevPosition !== initialPosition;
      console.log('DraggableImageBox: initialPosition changed:', {
        initialPosition,
        prevPosition,
        hasChanged,
        hasUserDragged
      });

      // If initialPosition is undefined, reset to default centered position
      if (!initialPosition) {
        console.log('DraggableImageBox: Resetting to default centered position');
        if (boxRef.current && imageSize.height > 0) {
          const boxRect_0 = boxRef.current.getBoundingClientRect();
          const centerX = 0; // Always center horizontally
          const centerY = imageSize.height > boxRect_0.height ? 0 : (boxRect_0.height - imageSize.height) / 2;
          setPosition({
            x: centerX,
            y: centerY
          });
          setHasUserDragged(false);
          prevInitialPositionRef.current = initialPosition;
        }
      } else if (initialPosition) {
        // Only apply initialPosition if user hasn't dragged OR if this is a genuinely new initialPosition
        // (not just the same value being passed again after a save operation)
        if (!hasUserDragged || hasChanged) {
          console.log('DraggableImageBox: Applying initialPosition:', initialPosition);
          // Convert percentage to pixel coordinates for internal positioning
          const pixelPosition = convertPercentageToPixels(initialPosition);
          setPosition(pixelPosition);

          // Only reset hasUserDragged if this is a genuinely new initialPosition
          if (hasChanged) {
            setHasUserDragged(false);
            prevInitialPositionRef.current = initialPosition;
          }
        }
      }
    }
  }, [initialPosition, dragging, hasUserDragged, convertPercentageToPixels, imageSize.height]);

  // Get image dimensions when it loads and center it
  const onImageLoad = useCallback(() => {
    if (imageRef.current && boxRef.current) {
      const boxRect_1 = boxRef.current.getBoundingClientRect();

      // Since we set width: '100%', the actual rendered width is the container width
      // and height is calculated automatically to maintain aspect ratio
      const renderedWidth = boxRect_1.width;
      const renderedHeight = imageRef.current.naturalHeight / imageRef.current.naturalWidth * renderedWidth;
      setImageSize({
        width: renderedWidth,
        height: renderedHeight
      });

      // NEVER set position if we have an initialPosition - let the useEffect handle it
      if (!initialPosition && !hasUserDragged) {
        // Position image to fill container without showing blank space
        const centerX_0 = 0; // Always center horizontally

        // If image is taller than container, start at top (y: 0)
        // If image is shorter than container, center it vertically
        const centerY_0 = renderedHeight > boxRect_1.height ? 0 : (boxRect_1.height - renderedHeight) / 2;
        setPosition({
          x: centerX_0,
          y: centerY_0
        });
      }
    }
  }, [initialPosition, hasUserDragged]);

  // Constrain position within bounds
  const constrainPosition = useCallback((pos_0: {
    x: number;
    y: number;
  }) => {
    const bounds_1 = getBounds();
    return {
      x: 0,
      // Always keep X at 0 (centered)
      y: Math.max(bounds_1.minY, Math.min(bounds_1.maxY, pos_0.y))
    };
  }, [getBounds]);

  // Reset image to center position
  const resetPosition = useCallback(() => {
    if (initialPosition) {
      // Reset to the saved initial position (convert percentage to pixels)
      const pixelPosition_0 = convertPercentageToPixels(initialPosition);
      setPosition(pixelPosition_0);
      setHasUserDragged(false);
    } else if (boxRef.current) {
      // Fall back to default centered position
      const boxRect_2 = boxRef.current.getBoundingClientRect();
      const centerX_1 = 0; // Always center horizontally

      // If image is taller than container, start at top (y: 0)
      // If image is shorter than container, center it vertically
      const centerY_1 = imageSize.height > boxRect_2.height ? 0 : (boxRect_2.height - imageSize.height) / 2;
      setPosition({
        x: centerX_1,
        y: centerY_1
      });
    }
  }, [initialPosition, imageSize, convertPercentageToPixels]);
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setHasUserDragged(true);
    setStartDrag({
      x: e.clientX,
      y: e.clientY
    });
    setStartPos({
      ...position
    });
  }, [position]);
  const onMouseMove = useCallback((e_0: MouseEvent) => {
    if (!dragging) {
      return;
    }
    const dy = e_0.clientY - startDrag.y;
    // Only apply Y movement, keep X at 0
    const newPosition = constrainPosition({
      x: 0,
      y: startPos.y + dy
    });

    // Debug: Log position and bounds during dragging
    if (Math.abs(dy) > 5) {
      // Only log when there's significant movement
      const bounds_2 = getBounds();
      console.log('Drag debug:', {
        dy,
        startPosY: startPos.y,
        newPosY: newPosition.y,
        bounds: bounds_2,
        imageSizeHeight: imageSize.height
      });
    }
    setPosition(newPosition);
  }, [dragging, startDrag, startPos, constrainPosition, getBounds, imageSize.height]);
  const onMouseUp = useCallback(() => {
    setDragging(false);
    // Call onDragEnd with the final position when dragging ends
    if (onDragEnd && !disableDragEnd) {
      const finalPosition = convertPositionToPercentage(position);
      isUpdatingFromParentRef.current = true;
      onDragEnd({
        x: 0,
        y: finalPosition
      });
      // Reset the flag after a short delay to allow parent to update
      setTimeout(() => {
        isUpdatingFromParentRef.current = false;
      }, 100);
    }
  }, [onDragEnd, position, convertPositionToPercentage, disableDragEnd]);

  // Touch events for mobile
  const onTouchStart = useCallback((e_1: React.TouchEvent) => {
    if (e_1.touches.length !== 1) {
      return;
    }
    e_1.preventDefault();
    setDragging(true);
    setHasUserDragged(true);
    setStartDrag({
      x: e_1.touches[0].clientX,
      y: e_1.touches[0].clientY
    });
    setStartPos({
      ...position
    });
  }, [position]);
  const onTouchMove = useCallback((e_2: TouchEvent) => {
    if (!dragging || e_2.touches.length !== 1) {
      return;
    }
    e_2.preventDefault();
    const dy_0 = e_2.touches[0].clientY - startDrag.y;
    // Only apply Y movement, keep X at 0
    const newPosition_0 = constrainPosition({
      x: 0,
      y: startPos.y + dy_0
    });
    setPosition(newPosition_0);
  }, [dragging, startDrag, startPos, constrainPosition]);
  const onTouchEnd = useCallback(() => {
    setDragging(false);
    // Call onDragEnd with the final position when touch dragging ends
    if (onDragEnd && !disableDragEnd) {
      const finalPosition_0 = convertPositionToPercentage(position);
      isUpdatingFromParentRef.current = true;
      onDragEnd({
        x: 0,
        y: finalPosition_0
      });
      // Reset the flag after a short delay to allow parent to update
      setTimeout(() => {
        isUpdatingFromParentRef.current = false;
      }, 100);
    }
  }, [onDragEnd, position, convertPositionToPercentage, disableDragEnd]);

  // Add/remove event listeners
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('touchmove', onTouchMove, {
        passive: false
      });
      window.addEventListener('touchend', onTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [dragging, onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);
  return <Box width={'full'}>
      <Flex align={'center'} aspectRatio={'16/9'} bgColor={'black'} borderRadius={'xl'} direction={'column'} justify={'center'} overflow={'hidden'} position={'relative'} width={'full'}>
        <Box bgColor={'blackAlpha.700'} height={'22%'} left={0} pointerEvents={'none'} position={'absolute'} top={0} width={'100%'} zIndex={1} />
        <Box aspectRatio={'16/5'} border={'2px solid'} borderColor={'colorPalette.fg'} pointerEvents={'none'} position={'absolute'} top={'50%'} transform={'translateY(-50%)'} width={'100%'} zIndex={2} />
        <Box ref={boxRef} aspectRatio={'16/5'} bg={'blackAlpha.200'} cursor={dragging ? 'grabbing' : 'grab'} height={height} position={'relative'} userSelect={'none'} width={width} {...boxProps}>
          <Image ref={imageRef} alt={alt} draggable={false} src={src} style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: '100%',
          // Make image width fill container
          height: 'auto',
          // Maintain aspect ratio
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none'
        }} onLoad={onImageLoad} onMouseDown={onMouseDown} onTouchStart={onTouchStart} {...imageProps} />
        </Box>
        {showResetButton && <HStack bottom={getBreakpointValue([2, 2, 5])} justify={'center'} left={getBreakpointValue([2, 2, 5])} mt={2} position={'absolute'} zIndex={2}>
            <Button _hover={{
          bgColor: 'black'
        }} bgColor={'blackAlpha.700'} color={'white'} size={getBreakpointValue(['xs', 'xs', 'sm'])} onClick={resetPosition}>
              <LuRotateCcw />
              Reset
            </Button>
          </HStack>}
        <Box bgColor={'blackAlpha.700'} bottom={0} height={'22%'} left={0} pointerEvents={'none'} position={'absolute'} width={'100%'} zIndex={1} />
      </Flex>
    </Box>;
};